import { socketUrl } from './serverUrl.js';
import { ServerMessageSchema, parseServerMessage, type ClientMessage, type ServerMessage } from './shared.js';

/** Os tipos de mensagem que o servidor desta versão manda. */
const SERVER_TYPES = new Set<string>(ServerMessageSchema.options.map((option) => option.shape.type.value));

/** A mensagem tem um tipo que esta versão conhece? (um tipo novo, de um servidor mais novo, não é erro) */
function isKnownServerType(raw: unknown): boolean {
  const type = (raw as { type?: unknown } | null)?.type;
  return typeof type !== 'string' || SERVER_TYPES.has(type);
}

export type SocketStatus = 'offline' | 'connecting' | 'online';

export type SocketHandlers = {
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: SocketStatus) => void;
  onProblem: (message: string) => void;
  /**
   * A conexão fechou sem ter aberto. O navegador não diz por quê (o 401 do servidor para uma
   * sessão que não vale mais vira só `close`): quem usa confere a sessão.
   */
  onRefused?: () => void;
};

/**
 * Cliente WebSocket com reconexão automática.
 *
 * A conexão fica aberta durante toda a sessão (contatos, mensagens diretas e
 * presença chegam por ela) e apenas AUTENTICA (recebe `hello`). Entrar numa
 * sala é um passo separado, enviado pela store (`room.join`), onde o servidor
 * confere a senha da sala — por isso a senha nunca aparece na URL.
 *
 * A reconexão é simples porque o servidor reenvia o estado completo da sala em
 * `ready`: reconectar é reentrar. Nada precisa ser ressincronizado no cliente.
 */
export class RpgDeskSocket {
  private socket: WebSocket | null = null;
  private token: string | null = null;
  private attempt = 0;
  private reconnectTimer: number | null = null;
  private closedByUser = false;
  private pending: ClientMessage[] = [];

  constructor(private readonly handlers: SocketHandlers) {}

  connect(token: string): void {
    this.closedByUser = false;
    this.token = token;
    this.open();
  }

  private open(): void {
    if (!this.token) return;

    this.cleanupSocket();
    this.handlers.onStatus('connecting');

    const socket = new WebSocket(socketUrl(this.token));
    this.socket = socket;
    // um socket já trocado (ou fechado de propósito) pode ainda soltar eventos: o `close` atrasado
    // dele agendaria uma reconexão que derrubaria o atual. Os ouvintes só valem para o atual
    const current = () => this.socket === socket;

    let opened = false;
    socket.addEventListener('open', () => {
      if (!current()) return;
      opened = true;
      this.attempt = 0;
      this.handlers.onStatus('online');
      // a fila espera o `flush()`: o que foi feito offline só vale depois de reentrar na sala
    });

    socket.addEventListener('message', (event) => {
      if (!current()) return;
      let raw: unknown;
      try {
        raw = JSON.parse(String(event.data));
      } catch {
        this.handlers.onProblem('o servidor enviou algo que não é JSON');
        return;
      }
      const parsed = parseServerMessage(raw);
      if (!parsed.ok) {
        // tipo que esta versão não conhece (servidor mais novo, durante a troca de versão): ignora
        if (!isKnownServerType(raw)) return;
        this.handlers.onProblem(`mensagem fora do protocolo: ${parsed.error}`);
        return;
      }
      this.handlers.onMessage(parsed.value);
    });

    socket.addEventListener('close', () => {
      if (!current()) return;
      this.handlers.onStatus('offline');
      if (!this.closedByUser) this.scheduleReconnect();
      if (!opened && !this.closedByUser) this.handlers.onRefused?.();
    });

    socket.addEventListener('error', () => {
      // o evento de erro é sempre seguido de close; o tratamento fica lá
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    this.attempt += 1;
    // backoff exponencial com teto de 10 s
    const delay = Math.min(10_000, 500 * 2 ** Math.min(this.attempt, 5));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  send(message: ClientMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
      return;
    }
    // sem sessão (saiu da conta): não há conexão para voltar, e guardar faria a mensagem sair na
    // conta seguinte desta aba
    if (this.token === null) return;
    // guarda a intenção: ela sai assim que a conexão voltar
    this.pending.push(message);
    if (this.pending.length > 50) this.pending.shift();
  }

  /**
   * Manda o que ficou guardado enquanto a conexão estava fora. Quem usa chama depois do
   * `hello` (sem sala para reentrar) ou do `ready` (de volta à sala): mandar antes faria o
   * servidor recusar as ações da sala ("entre em uma sala primeiro") e elas se perderiam.
   */
  flush(): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    for (const message of this.pending.splice(0)) socket.send(JSON.stringify(message));
  }

  /**
   * Esquece o que esperava a conexão voltar. A fila não sabe de que mesa cada mensagem é: a store
   * chama ao trocar de mesa e ao sair dela, senão o que foi feito numa mesa sairia na seguinte.
   */
  dropPending(): void {
    this.pending = [];
  }

  /** Conexão aberta agora (mensagens saem na hora). */
  isOpen(): boolean {
    return !!this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  /** Há uma conexão pedida (aberta, abrindo ou esperando para reconectar). */
  isActive(): boolean {
    return this.token !== null;
  }

  /** Encerra a sessão (logout): fecha o socket e não reconecta. */
  close(): void {
    this.closedByUser = true;
    this.token = null;
    this.pending = [];
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.cleanupSocket();
    this.handlers.onStatus('offline');
  }

  private cleanupSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (!socket) return;
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onopen = null;
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close();
    }
  }
}
